import React, { Suspense, useEffect, useState } from 'react';
import ApiClient from "./service_clients/ApiClient";
import DeepMerge from "./utils/DeepMerge";
import ShouldSeeNobi from "./utils/ShouldSeeNobi";
import DefaultStyles from "./utils/DefaultStyles";
import DefaultAssistantConfiguration from "./utils/DefaultAssistantConfiguration";
import Events from "./utils/Events";
import Constants from "./utils/Constants";
import NobiEventTracker from "./utils/NobiEventTracker";
import {EntryPoint as EntryPoint} from "./components/Chatbot/EntryPoint";
import FocusOnTop from "./utils/FocusOnTop";
import {v4 as uuidv4} from 'uuid';

const SearchBarInjector = React.lazy(() => import('./components/SearchBarInjector/SearchBarInjector'));
const ChatDrawer = React.lazy(() => import('./components/ChatDrawer/ChatDrawer'));
const ShopWithAiButtonInjector = React.lazy(() => import('./components/ShopWithAiButtonInjector/ShopWithAiButtonInjector'));
const CartAssistantInjector = React.lazy(() => import('./components/CartAssistantInjector/CartAssistantInjector'));
const PdpAssistantInjector = React.lazy(() => import('./components/PdpAssistantInjector/PdpAssistantInjector'));

/**
 * Main application component
 *
 * @param merchantId The merchant ID
 * @constructor
 */
const App = ({ merchantId, providedAssistantConfiguration, debugMode, registerOnProductAddedToCart }) => {
    const isTestMode = window.location.href.includes("#nobi-test") || debugMode;
    ApiClient.setDebugMode(isTestMode);

    const [initialUserMessage, setInitialUserMessage] = useState(null);
    const [initialAssistantMessage, setInitialAssistantMessage] = useState(null);
    const [displaySearchDrawer, setDisplaySearchDrawer] = useState(false);
    const [isSearchBarVisible, setIsSearchBarVisible] = useState(false);
    const [brandTheme, setBrandTheme] = useState(null);
    const [assistantConfiguration, setAssistantConfiguration] = useState(null);
    const [merchant, setMerchant] = useState(null);
    const [entryPoint, setEntryPoint] = useState(null);
    const [initialProductCategories, setInitialProductCategories] = useState(null);
    const [cartProductVariantInternalId, setCartProductVariantInternalId] = useState(null);
    const [cartProductInternalId, setCartProductInternalId] = useState(null);

    const log = (message) => {
        if (process.env.NODE_ENV !== "production" || isTestMode) {
            console.log(message);
        }
    };

    // Fetch merchant data and initialize Nobi
    useEffect(() => {
        if (!window.Nobi) {
            window.Nobi = {};
        }

        setShopperUniqueIdIfNeeded();

        if (!window.Nobi.merchant) {
            ApiClient.getMerchant(merchantId, (merchant, error) => {
                if (error || !merchant) {
                    console.error("Error fetching merchant data:", error);
                    return;
                }
                log('Merchant data fetched successfully:');
                log(merchant);
                window.Nobi.merchant = merchant;
                initializeNobiElements(merchant);
            });
        }
        else {
            log('Merchant data already fetched, initializing Nobi elements');
            initializeNobiElements(window.Nobi.merchant);
        }
    }, [merchantId]);

    // Handle Escape key to close the search bar when visible
    useEffect(() => {
        if (!isSearchBarVisible) {
            return;
        }

        const handleKeyDown = (event) => {
            if (event.key === 'Escape' && isSearchBarVisible) {
                resetSearchStateVariables(true);
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isSearchBarVisible]);

    function setShopperUniqueIdIfNeeded() {
        let nobiData = localStorage.getItem(Constants.MasterLocalStorageKey);
        nobiData = nobiData ? JSON.parse(nobiData) : {};
        if (nobiData[Constants.KeyForShopperIdInLocalStorage]) {
            return; // Shopper ID already set
        }
        nobiData[Constants.KeyForShopperIdInLocalStorage] = uuidv4(); // Generate a new unique ID
        localStorage.setItem(Constants.MasterLocalStorageKey, JSON.stringify(nobiData));
    }

    function initializeNobiElements(merchant) {
        setMerchant(merchant);
        updateBrandTheme(merchant);
        updateAssistantConfiguration(merchant);
    }

    function updateBrandTheme(merchant) {
        let styles = DefaultStyles;
        if (merchant?.styles) {
            styles = DeepMerge(styles, merchant.styles);
        }
        setBrandTheme(styles);
    }

    function updateAssistantConfiguration(merchant) {
        if (!merchant) {
            console.warn('Merchant data not set, not setting assistant configuration');
            return;
        }

        let configuration = DefaultAssistantConfiguration;
        if (merchant?.assistantConfiguration) {
            configuration = DeepMerge(configuration, merchant.assistantConfiguration);
        }

        if (providedAssistantConfiguration) {
            configuration = DeepMerge(configuration, providedAssistantConfiguration);
        }

        setAssistantConfiguration(configuration);
    }

    const toggleSearchBar = () => {
        setIsSearchBarVisible(!isSearchBarVisible);

        if (window.innerWidth < Constants.MaxWidthForMobile) {
            // Focus on the same selector that we used to open the search bar
            const searchIcons = document.querySelectorAll(assistantConfiguration.searchBar.searchIconSelector);
            if (searchIcons.length === 0) {
                console.error('No search icons found to focus on');
                FocusOnTop();
            }
            else {
                let searchIconFound = true;
                for (let icon of searchIcons) {
                    if (icon.offsetParent !== null) {
                        icon.focus();
                        searchIconFound = true;
                        break;
                    }
                }

                if (!searchIconFound) {
                    console.error('No visible search icons found to focus on');
                    FocusOnTop();
                }
            }
        }
    };

    const handleCloseDrawer = () => {
        const event = { query: initialUserMessage };
        NobiEventTracker.trackEvent(merchantId, entryPoint, Events.CloseDrawer, false, null, null, event);
        resetSearchStateVariables(false);
    };

    const openChatDrawer = (entryPoint) => {
        setDisplaySearchDrawer(true);
        setEntryPoint(entryPoint);
        if (entryPoint === EntryPoint.SearchBar) {
            setCartProductVariantInternalId(null);
            if (window.innerWidth < Constants.MaxWidthForMobile) {
                setIsSearchBarVisible(false);
            }
        }
    };

    function resetSearchStateVariables(closeSearchBar) {
        if (closeSearchBar) {
            setIsSearchBarVisible(false);
        }
        setInitialUserMessage('');
        setDisplaySearchDrawer(false);
    }

    const closeSearchBar = () => resetSearchStateVariables(true);

    function renderSearchBarInjector() {
        if (!merchant) {
            return null;
        }

        let shouldSeeSearchBar = ShouldSeeNobi(merchant, EntryPoint.SearchBar);
        return (shouldSeeSearchBar && assistantConfiguration?.searchBar &&
            <Suspense fallback={null}>
                <SearchBarInjector
                    merchantId={merchantId}
                    isSearchBarVisible={isSearchBarVisible}
                    onQueryUpdated={setInitialUserMessage}
                    onCloseSearchBar={closeSearchBar}
                    brandTheme={brandTheme}
                    assistantConfiguration={assistantConfiguration}
                    onStartSearch={toggleSearchBar}
                    onSearchSubmitted={() => openChatDrawer(EntryPoint.SearchBar)}
                    log={log}
                    isDrawerOpen={displaySearchDrawer}
                />
            </Suspense>
        );
    }

    function renderChatDrawer() {
        return (
            <Suspense fallback={null}>
                <ChatDrawer
                    merchant={merchant}
                    isOpen={displaySearchDrawer}
                    initialUserMessage={initialUserMessage}
                    initialAssistantMessage={initialAssistantMessage}
                    initialCategories={initialProductCategories}
                    onClose={handleCloseDrawer}
                    brandTheme={brandTheme}
                    log={log}
                    entryPoint={entryPoint}
                    assistantConfiguration={assistantConfiguration}
                    cartProductVariantInternalId={cartProductVariantInternalId}
                />
            </Suspense>
        );
    }

    function renderShopWithAiButtonInjector() {
        if (!merchant) {
            return null;
        }

        let shouldSeeShopWithAiButton = ShouldSeeNobi(merchant, EntryPoint.ShopWithAiButton);
        return (shouldSeeShopWithAiButton && assistantConfiguration?.shopWithAiButtonContainerSelector &&
            <Suspense fallback={null}>
                <ShopWithAiButtonInjector
                    merchantId={merchantId}
                    assistantConfiguration={assistantConfiguration}
                    brandTheme={brandTheme}
                    onClick={() => openChatDrawer(EntryPoint.ShopWithAiButton)}
                    log={log}
                />
            </Suspense>
        );
    }

    const handleMessageSentFromOutsideChatDrawer = (assistantMessage, userMessage, initialProductCategories, entryPoint) => {
        setInitialAssistantMessage(assistantMessage);
        setInitialUserMessage(userMessage);
        setInitialProductCategories(initialProductCategories);

        let newEntryPoint = entryPoint;
        if (entryPoint === EntryPoint.Cart) {
            newEntryPoint = EntryPoint.RespondingFromCartAssistant;
        }
        else if (entryPoint === EntryPoint.Pdp) {
            newEntryPoint = EntryPoint.RespondingFromPdpAssistant;
        }

        openChatDrawer(newEntryPoint);

        let eventDetails = {
            icebreaker: assistantMessage,
            message: userMessage,
            entryPoint: entryPoint
        }
        NobiEventTracker.trackEvent(merchantId, entryPoint, Events.RespondToCartAssistant, false, true, true, eventDetails);
    };

    function renderCartAssistantInjector() {
        if (!merchant) {
            return null;
        }

        let shouldSeeCartAssistant = ShouldSeeNobi(merchant, EntryPoint.Cart);
        return (shouldSeeCartAssistant && assistantConfiguration?.cartAssistant &&
            <Suspense fallback={null}>
                <CartAssistantInjector
                    merchant={merchant}
                    assistantConfiguration={assistantConfiguration}
                    brandTheme={brandTheme}
                    log={log}
                    onSendMessage={window.innerWidth > Constants.MaxWidthForMobile ? handleMessageSentFromOutsideChatDrawer : null}
                    setCartProductVariantInternalId={setCartProductVariantInternalId}
                    cartProductVariantInternalId={cartProductVariantInternalId}
                    setCartProductInternalId={setCartProductInternalId}
                    cartProductInternalId={cartProductInternalId}
                    registerOnProductAddedToCart={registerOnProductAddedToCart}
                />
            </Suspense>
        );
    }

    function renderPdpAssistantInjector() {
        if (!merchant || !assistantConfiguration?.pdpSectionSelector) {
            return null;
        }

        let shouldSeePdpAssistant = ShouldSeeNobi(merchant, EntryPoint.Pdp);
        return (shouldSeePdpAssistant && assistantConfiguration?.pdpSectionSelector &&
            <Suspense fallback={null}>
                <PdpAssistantInjector
                    merchant={merchant}
                    assistantConfiguration={assistantConfiguration}
                    brandTheme={brandTheme}
                    log={log}
                    onSendMessage={(userMessage) => {
                        handleMessageSentFromOutsideChatDrawer(null, userMessage, null, EntryPoint.Pdp);
                    }}
                />
            </Suspense>
        );
    }

    return (merchant && assistantConfiguration && (
            <div id="nobi-search-components-container">
                {renderSearchBarInjector()}
                {renderChatDrawer()}
                {renderShopWithAiButtonInjector()}
                {renderCartAssistantInjector()}
                {renderPdpAssistantInjector()}
            </div>
        )
    );
};

export default App;
